import * as React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { config } from '../Workspace/Dashboard/helpers/defaultDashboardConfig';

// redux
import { useDispatch } from 'react-redux';
import { workspaceActions } from '../Workspace/WorkspaceActions';

// mui
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import ListItemIcon from '@mui/material/ListItemIcon';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import StarRateIcon from '@mui/icons-material/StarRate';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import BoltIcon from '@mui/icons-material/Bolt';
import BrightnessLowIcon from '@mui/icons-material/BrightnessLow';
import CloudIcon from '@mui/icons-material/Cloud';
import FilterVintageIcon from '@mui/icons-material/FilterVintage';
import { TransitionProps } from '@mui/material/transitions';
import Slide from '@mui/material/Slide';

interface ICreateWorkspaceProps {
    open: boolean;
    handleCloseDialog: () => void;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const CreateWorkspaceDialog: React.FC<ICreateWorkspaceProps> = ({
    open,
    handleCloseDialog,
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [data, setData] = useState({ name: '', description: '', icon: '' });

    const iconList = [
        {
            key: 'star',
            value: <StarRateIcon />,
        },
        {
            key: 'heart',
            value: <FavoriteIcon />,
        },
        {
            key: 'autoGraph',
            value: <AutoGraphIcon />,
        },
        {
            key: 'moon',
            value: <BedtimeIcon />,
        },
        {
            key: 'lightning',
            value: <BoltIcon />,
        },
        {
            key: 'sun',
            value: <BrightnessLowIcon />,
        },
        {
            key: 'cloud',
            value: <CloudIcon />,
        },
        {
            key: 'flower',
            value: <FilterVintageIcon />,
        },
    ];

    const handleChange = (event: SelectChangeEvent) => {
        const value = event.target.value;
        setData({
            ...data,
            icon: value,
        });
    };

    const handleTextChange = (event: { target: { value: any; name: any } }) => {
        const value = event.target.value;
        const name = event.target.name;
        setData({
            ...data,
            [name]: value,
        });
    };

    const handleCancel = () => {
        setData({ name: '', description: '', icon: '' });
        handleCloseDialog();
    };

    const handleSaveWorkspace = () => {
        dispatch(workspaceActions?.createWorkspace(data)).then((res) => {
            navigate(`/workspace/${res.id}`);
            dispatch(
                workspaceActions?.setWorkspaceConfiguration(
                    res.id,
                    'dashboard-configuration',
                    config
                )
            );
        });
        handleCloseDialog();
    };

    return (
        <Dialog
            open={open}
            onClose={handleCloseDialog}
            maxWidth={'sm'}
            fullWidth
            TransitionComponent={Transition}
            data-testid={'create-workspace-dialog'}
        >
            <DialogTitle>Create New Workspace</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    id="name"
                    label="Workspace Name"
                    name={'name'}
                    fullWidth
                    variant="outlined"
                    required
                    onChange={handleTextChange}
                />
                <TextField
                    margin="dense"
                    id="description"
                    label="Workspace Description"
                    fullWidth
                    variant="outlined"
                    name={'description'}
                    onChange={handleTextChange}
                />
                <FormControl fullWidth sx={{ marginTop: 1 }}>
                    <InputLabel id="demo-simple-select-label">Icon</InputLabel>
                    <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={data.icon}
                        label="Icon"
                        name={'icon'}
                        onChange={handleChange}
                        inputProps={{ 'data-testid': 'icon-selection' }}
                    >
                        {iconList.map((icon, i) => {
                            return (
                                <MenuItem value={icon.key} key={icon.key}>
                                    <ListItemIcon>{icon.value}</ListItemIcon>
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel}>Cancel</Button>
                <Button
                    onClick={handleSaveWorkspace}
                    variant={'contained'}
                    color={'primary'}
                    disabled={!data.name}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateWorkspaceDialog;
